// The form is a 6-step wizard instead of one long scroll: the rule model got big enough that a
// flat drawer buried the fields that matter. Steps 1 and 2 are the minimum viable rule, so saving
// is allowed from step 2 onwards; steps 3 and 4 are guards that can be filled in later.
//
// `errorKeys` maps each step to the keys its fields publish into `dsRepricingDrawerErrors`, which
// is what lets the footer block Next on the current step without dragging in unrelated steps.
// `kicker` is the verb that names each step's role in the story of a rule:
// When → What → How → Then. Rendered above the label in the stepper.
export const DS_REPRICING_STEPS = [
	{
		step: 1,
		kicker: 'WHAT',
		label: 'Basic',
		sub: 'What this rule is, which product it prices and how often it runs',
		// Identity, the product and the cadence (CU-869fhuhy6, brief v3): what the rule IS. Which
		// pickups it reprices is a selector, a different concept, and has its own step. The product
		// is required like the name; `cadence` validates like the old schedule rule.
		errorKeys: ['name', 'priority', 'product', 'cadence'],
	},
	{
		step: 2,
		kicker: 'DATES & LOR',
		label: 'Dates & LOR',
		sub: 'Which pickup dates and rental lengths it reprices',
		// `leadTimes` and `seasons` are the two ways of saying when the rule applies; the inactive
		// one publishes valid, so both keys can be listed. No 'weekdays': an optional filter has
		// no invalid state.
		errorKeys: ['seasons', 'durations', 'leadTimes'],
	},
	{
		step: 3,
		kicker: 'WHERE',
		// "Scope", not "Market scope": the step holds what the rule prices (our
		// offices, our faces, our vehicles) as well as what it is compared
		// against, and a step titled "market" labelled everything inside it
		// as market — which is how "Insurance" would have read as "compare
		// only against this insurance" (CU-869fe6mf4, UX decision 2).
		label: 'Scope',
		sub: 'Where this rule prices, and which market — and kinds of offer — it is compared against',
		// Scope fields never fail validation today, but the step still lists the keys so future
		// per-field errors show up as an invalid step in the stepper. No tariff type / insurance
		// since brief v3: the product says which of our rows the rule prices. `compareAgainst`
		// (card 6, "Which kinds of offer", UX v3 D18) fails when "a specific kind" names no axis.
		errorKeys: ['filterGroupOffices', 'filterGroupSites', 'filterGroupWebSuppliers',
		            'filterGroupProviders', 'filterAcriss', 'filterCategories', 'compareAgainst'],
	},
	{
		step: 4,
		kicker: 'HOW',
		label: 'Pricing strategy',
		sub: 'Market Trend or Ranking, the undercut and the competitor guard',
		errorKeys: ['strategy', 'undercutValue'],
	},
	{
		step: 5,
		kicker: 'THEN',
		label: 'Tethering',
		sub: 'Pick which tethers from Setup should apply',
		optional: true,
		errorKeys: [],
	},
	{
		step: 6,
		kicker: 'IF',
		label: 'My Business',
		sub: 'PMS activation conditions (fleet, pace, forecast…)',
		optional: true,
		errorKeys: [],
	},
]

export const DS_REPRICING_TOTAL_STEPS = DS_REPRICING_STEPS.length

// Saving needs identity + when + scope + strategy in place; Extras are optional by design.
export const FIRST_SAVEABLE_STEP = 4

/** The step and card that hold "When does it run?" — where the alignment hint and the table send the operator. */
export const WHEN_IT_RUNS_STEP = 1
export const WHEN_IT_RUNS_CARD = 'when-it-runs'

const isOk = value => value !== false

export const stepIsValid = (step, errors) => {
	const definition = DS_REPRICING_STEPS.find(s => s.step === step)
	if (!definition) return true
	return definition.errorKeys.every(key => isOk(errors?.[key]))
}

// Every panel stays mounted (just hidden), so an untouched step still publishes its errors and a
// broken optional guard cannot slip through an early save.
export const formIsValid = errors => Object.values(errors ?? {}).every(isOk)